<template>
  <v-container fluid class="waste-container pa-3 pa-md-4">
    <v-btn
      variant="text"
      prepend-icon="mdi-arrow-left"
      class="mb-2 px-0"
      :to="{ name: 'waste' }"
    >
      {{ $t("waste.detail.back") }}
    </v-btn>

    <div v-if="loading">
      <v-skeleton-loader type="article" />
    </div>

    <v-alert v-else-if="notFound" type="warning" variant="tonal">
      {{ $t("waste.detail.not_found") }}
    </v-alert>

    <v-alert v-else-if="error" type="error" variant="tonal">
      <div class="d-flex flex-wrap align-center ga-2">
        <span class="flex-grow-1">{{ error }}</span>
        <v-btn variant="outlined" size="small" @click="load">
          {{ $t("waste.retry") }}
        </v-btn>
      </div>
    </v-alert>

    <template v-else-if="header">
      <v-alert
        v-if="conflict"
        type="warning"
        variant="tonal"
        closable
        class="mb-3"
        @click:close="conflict = false"
      >
        {{ $t("waste.detail.conflict") }}
      </v-alert>
      <v-alert
        v-if="actionError"
        type="error"
        variant="tonal"
        closable
        class="mb-3"
        @click:close="actionError = ''"
      >
        {{ actionError }}
      </v-alert>

      <div class="d-flex flex-wrap align-center ga-2 mb-1">
        <h1 class="text-h6 text-md-h5 text-wrap">{{ header.name }}</h1>
        <v-chip v-if="header.inactive" size="small">
          {{ $t("waste.status.inactive") }}
        </v-chip>
      </div>
      <div class="text-body-1 mb-1 text-wrap">
        <span class="font-weight-bold">{{ header.code }}</span>
        {{ header.description }}
        <v-chip
          v-if="header.hazardous"
          size="x-small"
          color="red"
          variant="tonal"
          class="ml-1"
        >
          {{ $t("waste.hazardous") }}
        </v-chip>
      </div>
      <div class="text-body-2 text-medium-emphasis mb-3">
        {{ $t(header.typeKey) }} · {{ $t(header.unitKey) }}
        <span v-if="header.tare">
          · {{ $t("waste.detail.tare", { kg: header.tare }) }}
        </span>
      </div>
      <div v-if="header.note" class="text-body-2 mb-3 text-wrap">
        {{ header.note }}
      </div>

      <div class="d-flex flex-wrap ga-2 mb-4">
        <v-btn
          variant="outlined"
          prepend-icon="mdi-qrcode"
          :loading="printing"
          @click="printLabel"
        >
          {{ $t("waste.detail.print_label") }}
        </v-btn>
        <v-btn
          v-if="actions.edit"
          variant="outlined"
          color="primary"
          prepend-icon="mdi-pencil"
          @click="dialog = true"
        >
          {{ $t("waste.detail.edit") }}
        </v-btn>
        <v-btn
          v-if="actions.deactivate || actions.reactivate"
          variant="outlined"
          :color="actions.deactivate ? 'red-darken-3' : 'green-darken-2'"
          :prepend-icon="actions.deactivate ? 'mdi-archive' : 'mdi-restore'"
          :loading="statusSaving"
          @click="confirmStatus = true"
        >
          {{
            $t(
              actions.deactivate
                ? "waste.detail.deactivate"
                : "waste.detail.reactivate",
            )
          }}
        </v-btn>
      </div>

      <v-card variant="outlined" class="mb-4">
        <v-card-title>{{ $t("waste.detail.content_title") }}</v-card-title>
        <v-card-text>
          <div class="text-h6">
            {{ $t(header.recorded.key, header.recorded.params) }}
          </div>
          <div
            v-if="detail.container.type !== 'area'"
            class="text-caption text-medium-emphasis mb-2"
          >
            {{ $t("waste.detail.not_converted") }}
          </div>
          <v-chip size="small" variant="tonal" :color="header.weighing.color">
            {{ $t(header.weighing.key, header.weighing.params) }}
          </v-chip>
          <div
            v-if="header.weighingStale"
            class="text-caption text-orange-darken-3 mt-1"
          >
            {{ $t("waste.detail.weighing_stale") }}
          </div>
          <div class="d-flex flex-wrap ga-2 mt-3">
            <v-btn
              v-if="actions.weigh"
              variant="outlined"
              color="primary"
              prepend-icon="mdi-scale"
              @click="weighDialog = true"
            >
              {{ $t("waste.detail.weigh") }}
            </v-btn>
            <v-btn
              v-if="actions.sendOut"
              variant="flat"
              color="primary"
              prepend-icon="mdi-truck-outline"
              @click="sendOutDialog = true"
            >
              {{ $t("waste.detail.send_out") }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <!-- Stage 6 slice 2: the current filling's entries from depollution. -->
      <v-card
        v-if="detail.container.type !== 'area'"
        variant="outlined"
        class="mb-4"
      >
        <v-card-title>{{ $t("waste.detail.received_title") }}</v-card-title>
        <v-card-text>
          <div v-if="!entryRows.length" class="text-body-2">
            {{ $t("waste.detail.received_empty") }}
          </div>
          <template v-else>
            <v-table v-if="desktop" density="compact">
              <thead>
                <tr>
                  <th>{{ $t("waste.detail.columns.vehicle") }}</th>
                  <th>{{ $t("waste.detail.columns.task") }}</th>
                  <th class="text-right">
                    {{ $t("waste.detail.columns.quantity") }}
                  </th>
                  <th>{{ $t("waste.detail.columns.basis") }}</th>
                  <th>{{ $t("waste.detail.columns.date") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in entryRows"
                  :key="row.id"
                  :class="{ 'text-medium-emphasis': row.reversed }"
                >
                  <td>
                    <router-link
                      v-if="row.dossierId"
                      :to="{
                        name: 'acceptance',
                        params: { dossierId: row.dossierId },
                      }"
                    >
                      {{ row.vehicle }}
                    </router-link>
                    <span v-else>{{ row.vehicle }}</span>
                    <div v-if="row.vehicleName" class="text-caption">
                      {{ row.vehicleName }}
                    </div>
                  </td>
                  <td>{{ row.taskKey ? $t(row.taskKey) : "—" }}</td>
                  <td class="text-right text-no-wrap">
                    <span
                      :class="{ 'text-decoration-line-through': row.reversed }"
                    >
                      {{ row.quantity }}
                    </span>
                  </td>
                  <td>{{ row.basisKey ? $t(row.basisKey) : "—" }}</td>
                  <td class="text-no-wrap">
                    {{ row.date }}
                    <v-chip
                      v-if="row.reversal || row.reversed"
                      size="x-small"
                      variant="tonal"
                      color="orange-darken-2"
                      class="ml-1"
                    >
                      {{
                        $t(
                          row.reversal
                            ? "waste.detail.reversal"
                            : "waste.detail.reversed",
                        )
                      }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </v-table>
            <v-list v-else density="compact" class="py-0">
              <v-list-item
                v-for="row in entryRows"
                :key="row.id"
                class="px-0"
                :class="{ 'text-medium-emphasis': row.reversed }"
              >
                <v-list-item-title class="text-wrap">
                  {{ row.vehicle }} ·
                  {{ row.taskKey ? $t(row.taskKey) : "—" }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-wrap">
                  <span
                    :class="{ 'text-decoration-line-through': row.reversed }"
                  >
                    {{ row.quantity }}
                  </span>
                  <span v-if="row.basisKey"> · {{ $t(row.basisKey) }}</span>
                  · {{ row.date }}
                </v-list-item-subtitle>
                <template v-if="row.reversal || row.reversed" #append>
                  <v-chip
                    size="x-small"
                    variant="tonal"
                    color="orange-darken-2"
                  >
                    {{
                      $t(
                        row.reversal
                          ? "waste.detail.reversal"
                          : "waste.detail.reversed",
                      )
                    }}
                  </v-chip>
                </template>
              </v-list-item>
            </v-list>
            <v-btn
              v-if="moreCount"
              variant="text"
              color="primary"
              class="mt-2"
              :loading="loadingMore"
              @click="loadMore"
            >
              {{ $t("waste.detail.more", { count: moreCount }) }}
            </v-btn>
            <div v-if="moreError" class="text-caption text-error mt-1">
              {{ moreError }}
            </div>
          </template>
        </v-card-text>
      </v-card>

      <!-- Stage 6 slice 3: weighings and send-outs of every filling, newest first. -->
      <v-card variant="outlined" class="mb-4">
        <v-card-title>{{ $t("waste.timeline.title") }}</v-card-title>
        <v-card-text>
          <div v-if="!timeline.length" class="text-body-2">
            {{ $t("waste.timeline.empty") }}
          </div>
          <v-list v-else density="compact" class="py-0">
            <v-list-item
              v-for="row in timeline"
              :key="row.kind + '-' + row.id"
              class="px-0"
              :prepend-icon="row.icon"
            >
              <v-list-item-title class="text-wrap">
                {{ $t(row.titleKey, row.params) }}
                <v-chip
                  v-if="row.current"
                  size="x-small"
                  color="green"
                  variant="tonal"
                  class="ml-1"
                >
                  {{ $t("waste.timeline.current") }}
                </v-chip>
              </v-list-item-title>
              <v-list-item-subtitle class="text-wrap">
                <template v-if="row.kind === 'weighing'">
                  {{ $t(row.methodKey) }} ·
                  {{ $t(row.proof.key, row.proof.params) }}
                </template>
                <template v-else>
                  {{ $t(row.content.key, row.content.params) }}
                  <span v-if="row.transporter">
                    ·
                    {{
                      $t("waste.timeline.transporter", {
                        name: row.transporter,
                      })
                    }}
                  </span>
                </template>
              </v-list-item-subtitle>
              <v-list-item-subtitle class="text-wrap">
                {{ row.date }}<span v-if="row.by"> · {{ row.by }}</span>
              </v-list-item-subtitle>
              <v-btn
                v-if="row.file"
                variant="text"
                size="small"
                color="primary"
                prepend-icon="mdi-paperclip"
                class="px-0 mt-1 text-none"
                @click="openFile(row.file)"
              >
                {{ row.file.name }}
              </v-btn>
            </v-list-item>
          </v-list>
          <div v-if="openError" class="text-caption text-error mt-1">
            {{ openError }}
          </div>
        </v-card-text>
      </v-card>
    </template>

    <weigh-container-dialog
      v-if="detail"
      v-model="weighDialog"
      :container="detail.container"
      :revision="detail.revision"
      @saved="onWritten"
      @conflict="onDialogConflict"
    />
    <send-out-dialog
      v-if="detail"
      v-model="sendOutDialog"
      :container="detail.container"
      :revision="detail.revision"
      @saved="onWritten"
      @conflict="onDialogConflict"
    />

    <container-dialog
      v-if="detail"
      v-model="dialog"
      :container="detail.container"
      :revision="detail.revision"
      :code-locked="detail.codeLocked"
      @saved="onSaved"
    />

    <v-dialog v-model="confirmStatus" max-width="420">
      <v-card v-if="header">
        <v-card-text class="pt-5">
          {{
            $t(
              actions.deactivate
                ? "waste.detail.confirm_deactivate"
                : "waste.detail.confirm_reactivate",
              { name: header.name },
            )
          }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="confirmStatus = false">
            {{ $t("waste.form.cancel") }}
          </v-btn>
          <v-btn color="red-darken-3" variant="flat" @click="toggleStatus">
            {{ $t("waste.detail.confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
// Container page (Stage 6 plan §D): header with the code, type, unit and tare; "Stampa etichetta",
// and for a Manager / Admin "Modifica" and "Disattiva / Riattiva" (canEdit from the API, which
// checks the role again). Writes run at the container revision; a conflict reloads and says so.
// Slice 2: "Ricevuto dalla bonifica", the current filling's entries (50 per page, "Altri n"), with
// the reversals of a redo or a correction marked.
// Slice 3: "Pesa" (every role), "Invia (svuota)" (Manager / Admin) and the timeline of weighings
// and send-outs with the send-out documents (private files, opened as object URLs).
import {
  getFileBlob,
  getWasteContainer,
  getWasteContainerEntries,
  newCommandId,
  updateWasteContainer,
} from "@/api/compliance";
import { createBlobCache } from "@/components/Acceptance/dossierFiles.mjs";
import ContainerDialog from "@/components/Waste/containerDialog.vue";
import SendOutDialog from "@/components/Waste/sendOutDialog.vue";
import WeighContainerDialog from "@/components/Waste/weighContainerDialog.vue";
import printContainerLabels from "@/components/Waste/print";
import {
  actionsView,
  entryRows,
  headerView,
  moreEntries,
  statusSet,
  timelineRows,
} from "@/components/Waste/wasteContainer.mjs";

export default {
  components: { ContainerDialog, SendOutDialog, WeighContainerDialog },
  data: () => ({
    detail: null,
    loading: true,
    notFound: false,
    error: "",
    actionError: "",
    conflict: false,
    dialog: false,
    confirmStatus: false,
    statusSaving: false,
    pendingStatusCommandId: "",
    printing: false,
    // The current filling's entries loaded so far: { items, page, total }.
    entries: { items: [], page: 1, total: 0 },
    loadingMore: false,
    moreError: "",
    weighDialog: false,
    sendOutDialog: false,
    openError: "",
  }),
  computed: {
    containerId() {
      return this.$route.params.containerId;
    },
    header() {
      return this.detail ? headerView(this.detail, this.$i18n.locale) : null;
    },
    actions() {
      return actionsView(this.detail);
    },
    desktop() {
      return this.$vuetify.display.mdAndUp;
    },
    entryRows() {
      return entryRows(this.entries, this.$i18n.locale);
    },
    moreCount() {
      return moreEntries(this.entries, this.entries.items.length);
    },
    timeline() {
      return timelineRows(
        this.detail && this.detail.timeline,
        this.$i18n.locale,
      );
    },
  },
  watch: {
    containerId() {
      this.blobs.revokeAll();
      this.load();
    },
  },
  created() {
    // Object URLs of the send-out documents, revoked on leave.
    this.blobs = createBlobCache({
      load: getFileBlob,
      revoke: (url) => URL.revokeObjectURL(url),
    });
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    this.blobs.revokeAll();
  },
  methods: {
    // quiet: keep the page on screen while it reloads after a write.
    async load({ quiet = false } = {}) {
      if (!quiet) this.loading = true;
      this.notFound = false;
      this.error = "";
      try {
        this.detail = await getWasteContainer(this.containerId);
        this.entries = this.detail.entries || { items: [], page: 1, total: 0 };
        this.moreError = "";
      } catch (err) {
        const status = err && err.response && err.response.status;
        if (status === 404) this.notFound = true;
        else this.error = this.errorText(err);
      } finally {
        this.loading = false;
      }
    },
    // The write answers leave the entries out: the loaded ones stay.
    onSaved(detail) {
      this.detail = this.keepFilling(detail);
      this.conflict = false;
    },
    // A write answer has no entries, so its vehicle count is 0: keep the loaded one.
    keepFilling(detail) {
      if (detail.entries || !this.detail) return detail;
      const before = this.detail.container.recorded || {};
      const recorded = {
        ...detail.container.recorded,
        vehicles: before.vehicles || 0,
      };
      return { ...detail, container: { ...detail.container, recorded } };
    },
    // A weighing or a send-out changes the filling and the timeline: reload the page state (a
    // send-out empties the container).
    async onWritten() {
      this.conflict = false;
      await this.load({ quiet: true });
    },
    async onDialogConflict() {
      this.conflict = true;
      await this.load({ quiet: true });
    },
    async openFile(file) {
      this.openError = "";
      // Opened before the download so the browser does not treat it as a popup.
      const tab = window.open("", "_blank");
      if (!tab) {
        this.openError = this.$t("acceptance.files.open_failed");
        return;
      }
      tab.opener = null;
      try {
        tab.location.href = await this.blobs.get(file.id);
      } catch (err) {
        tab.close();
        this.openError = this.$t("acceptance.files.open_failed");
      }
    },
    async loadMore() {
      this.loadingMore = true;
      this.moreError = "";
      try {
        const page = this.entries.page + 1;
        const { entries } = await getWasteContainerEntries(
          this.containerId,
          page,
        );
        this.entries = {
          items: [...this.entries.items, ...entries.items],
          page,
          total: entries.total,
        };
      } catch (err) {
        this.moreError = this.errorText(err);
      } finally {
        this.loadingMore = false;
      }
    },
    async toggleStatus() {
      this.confirmStatus = false;
      this.statusSaving = true;
      this.actionError = "";
      if (!this.pendingStatusCommandId)
        this.pendingStatusCommandId = newCommandId();
      try {
        const saved = await updateWasteContainer(
          this.detail.container.id,
          this.detail.revision,
          this.pendingStatusCommandId,
          statusSet(this.detail),
        );
        this.detail = this.keepFilling(saved);
        this.pendingStatusCommandId = "";
      } catch (err) {
        const code =
          err && err.response && err.response.data && err.response.data.code;
        if (err && err.response) this.pendingStatusCommandId = "";
        if (code === "revision_conflict") {
          this.conflict = true;
          await this.load();
        } else {
          this.actionError = this.errorText(err);
        }
      } finally {
        this.statusSaving = false;
      }
    },
    async printLabel() {
      this.printing = true;
      try {
        await printContainerLabels([this.detail.container]);
      } catch (err) {
        this.actionError = this.$t("waste.print_error");
      } finally {
        this.printing = false;
      }
    },
    errorText(err) {
      return (
        (err &&
          err.response &&
          err.response.data &&
          err.response.data.errorMsg) ||
        (err && err.message) ||
        this.$t("waste.load_error")
      );
    },
  },
};
</script>

<style scoped>
.waste-container {
  max-width: 960px;
  overflow-x: hidden;
}
</style>
